import React, { useState } from "react";
import { Button, Form, Select, Spin } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import * as XLSX from "xlsx";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import {
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

import FormField from "../../../common/Form/FormField";
import SelectField from "../../../common/Form/SelectField";
import { addReversementMass } from "../services/versementApi";
import { getBusinessByMarchandId } from "../../developer/services/developerApi";
import { generateUserId } from "../../../helpers/generateId";

import {
  DeleteTwoTone,
  PlusCircleFilled,
  UserAddOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";

import orangeIcon from "../../../assets/images/ORANGE.png";
import waveIcon from "../../../assets/images/WAVE.png";
import yasIcon from "../../../assets/images/YAS.png";
import "../utils/style.css";
import PhoneInput from "react-phone-input-2";

const { Option } = Select;

const PaiementMasse = ({ accounts }) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [activeTab, setActiveTab] = useState("2");
  const [selectedPaymentMethod, setSelectedPaaymentMethod] = useState("WAVESN");

  const currentUser = JSON.parse(sessionStorage.getItem("authUser"));
  const merchantId = currentUser?.user?.id;

  const { data: businessList, isLoading, isError, error } = useQuery(
    ["business", merchantId],
    () => getBusinessByMarchandId(merchantId),
    { enabled: !!merchantId }
  );

  const paiementMoyens = [
     { logo: waveIcon, name: "Wave", value: "WAVESN" },
    { logo: orangeIcon, name: "Orange Money", value: "OMSN" }
  ];

  const validationSchema = Yup.object().shape({
    businessId: Yup.string().required("Veuillez choisir votre business"),
    country: Yup.string().required("Veuillez choisir un pays"),
    receptionAccount: Yup.string().required("Veuillez choisir un compte"),
    clientUser: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Nom requis"),
          phone: Yup.string().required("Téléphone requis"),
          amount: Yup.number()
            .typeError("Montant invalide")
            .required("Montant requis"),
        })
      )
      .min(1, "Ajoutez au moins un bénéficiaire"),
  });

  const initialValues = {
    businessId: "",
    country: "",
    receptionAccount: "",
    clientUser: [
      {
        name: "",
        phone: "",
        amount: "",
        prefix: "",
        currency: "",
      },
    ],
  };

  const formik = useFormik({
    initialValues,
    // validationSchema,

    onSubmit: async (values) => {
      setIsLoadingSubmit(true);

      const data = {
        payOutCommandList: values.clientUser.map((user) => ({
          prefixPhone: user.prefix,
          phoneNumber: "+221" + user.phone,
          country: "SN",
          name: user.name,
          email: currentUser?.email || "support@paxity.io",
          currency: "XOF",
          amount: user.amount,
          paymentMethod: values.receptionAccount
        })),
        paymentGateway: values.receptionAccount
      };

      try {
        await addReversementMass(selectedBusiness, data);
        toast.success("Paiement en masse effectué avec succès !");
        formik.resetForm();
        setSelectedBusiness(null);
      } catch (err) {
        toast.error("Une erreur est survenue, veuillez réessayer.");
        console.error('Error =>', err?.message)
      } finally {
        setIsLoadingSubmit(false);
      }
    },
  });

  const handleBusinessChange = (e) => {
    const selected = businessList?.find((b) => String(b.id) === e.target.value);
    if (selected) {
      console.log(" === Selected Business === > ", selected);
      setSelectedBusiness(selected);
      formik.setFieldValue("businessId", selected.id);
      formik.setFieldValue("country", selected.country || "");
    }
  };

  const addClientUser = () => {
    formik.setFieldValue("clientUser", [
      ...formik.values.clientUser,
      {
        id: generateUserId(),
        name: "",
        phone: "",
        amount: "",
        prefix: "",
        surname: "",
        currency: "",
      },
    ]);
  };

  const handleExcelUpload = (event) => {
    console.log("Handle Excel Uploaded ...")
    const file = event.target.files[0];

    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target.result;

      const workbook = XLSX.read(data, { type: "binary" });

      const sheetName = workbook.SheetNames[0];

      const sheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(sheet);

      const formattedUsers = jsonData.map((row, index) => ({
        id: Date.now() + index,
        name: row.nom || "",
        prefix: row.code || "",
        phone: row.numero || "",
        amount: row.montant || ""
      }));

      console.log(" Formatted users > ", formattedUsers);

      formik.setFieldValue("clientUser", formattedUsers);
    };

    reader.readAsBinaryString(file);
  };

  return (
    <div className="add-rever-card" style={{ position: "relative" }}>
      {isLoadingSubmit && (
        <div className="overlay-spinner">
          <Spin size="large" />
        </div>
      )}
      <Form layout="vertical" onSubmitCapture={formik.handleSubmit}>
        <Row>
          <SelectField
            label="Business"
            name="businessId"
            formik={formik}
            colSize={6}
            nameSelect="votre business"
            options={businessList}
            onChange={handleBusinessChange}
            isLoading={isLoading}
            isError={isError}
            error={error}
          />

          <Col lg={6}>
            <div className="mb-3">
              <label htmlFor="country" className="form-label">
                Pays
              </label>
              <select
                id="country"
                name="country"
                className="form-control"
                onChange={formik.handleChange}
                value={formik.values.country}
              >
                <option value="">Choisir un pays</option>
                {businessList?.map((b) => (
                  <option key={b.id} value={b.country}>
                    {b.country}
                  </option>
                ))}
              </select>
              {formik.touched.country && formik.errors.country && (
                <div className="text-danger">{formik.errors.country}</div>
              )}
            </div>
          </Col>

          <FormField
            label="Solde disponible"
            value={selectedBusiness?.balance?.toLocaleString() || "-"}
            colSize={6}
            disabled
            placeholder="Lié à votre business"
          />

          <Col lg={6} className="mb-3">
            <label className="form-label">Mode d'envoi</label>
            <Select
              name="receptionAccount"
              value={formik.values.receptionAccount}
              onChange={(value) =>
                formik.setFieldValue("receptionAccount", value)
              }
              placeholder="Choisir votre moyen de réception"
              style={{ width: "100%" }}
              size="large"
            >
              {paiementMoyens.map((item) => (
                <Option key={item.value} value={item.value} disabled = {item.value == "OMSN" ? true : false}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={item.logo}
                      alt={item.name}
                      style={{ width: 20, height: 20, marginRight: 8 }}
                    />
                    <span>{item.name}</span>
                  </div>
                </Option>
              ))}
            </Select>
            {formik.touched.receptionAccount &&
              formik.errors.receptionAccount && (
                <div className="text-danger mt-1">
                  {formik.errors.receptionAccount}
                </div>
              )}
          </Col>

          <Col lg={12}>
            <Nav className="nav-tabs-custom mb-4">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "2" })}
                  onClick={() => setActiveTab("2")}
                >
                  <UsergroupAddOutlined /> Ajouter plusieurs destinataires
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "1" })}
                  onClick={() => setActiveTab("1")}
                >
                  <UserAddOutlined /> Ajouter un destinataire
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                {formik.values.clientUser.map((user, index) => (
                  <Row key={user.id} className="mb-2">
                    <div className="p-3 border rounded position-relative">
                      {formik.values.clientUser.length > 1 && (
                        <div
                          onClick={() => {
                            const updatedUsers = formik.values.clientUser.filter(
                              (u) => u.id !== user.id
                            );
                            formik.setFieldValue("clientUser", updatedUsers);
                          }}
                          className="custom-delete-btn position-absolute top-0 end-0 m-2"
                        >
                          <DeleteTwoTone twoToneColor="red" />
                        </div>
                      )}
                      <Row>
                        <FormField label="Prénom et Nom Bénéficiare" name={`clientUser[${index}].name`} formik={formik} colSize={6} type="text" />
                        <Col lg={6} className="mb-3">
                          <label className="form-label">Numéro du bénéficiare</label>
                          <PhoneInput
                            country={"sn"}
                            value={`${formik.values.clientUser[index].prefix}${formik.values.clientUser[index].phone}`}
                            onChange={(value, country) => {
                              const updatedUsers = [...formik.values.clientUser];
                              updatedUsers[index].prefix = country.dialCode;
                              updatedUsers[index].phone = value.replace(country.dialCode, "").trim();
                              formik.setFieldValue("clientUser", updatedUsers);
                            }}
                            inputStyle={{ width: "100%" }}
                            countryCodeEditable={false}
                          />

                          {formik.touched.clientUser?.[index]?.phone &&
                            formik.errors.clientUser?.[index]?.phone && (
                              <div className="text-danger mt-1">
                                {formik.errors.clientUser[index].phone}
                              </div>
                            )}
                        </Col>
                        <FormField label="Montant" name={`clientUser[${index}].amount`} formik={formik} colSize={12} type="number" />
                      </Row>
                    </div>
                  </Row>
                ))}

                <Button
                  className="btn-add-benef text-start"
                  onClick={addClientUser}
                  type="link"
                >
                  <PlusCircleFilled style={{ fontSize: "20px" }} /> Ajouter un autre bénéficiaire
                </Button>
              </TabPane>

              <TabPane tabId="2">
                <div className="w-full max-w-xl mx-auto p-4 sm:p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition">
                  <div className="flex flex-col items-center justify-center text-center space-y-3">

                    {/* Icône */}
                    <div className="text-gray-400 text-5xl sm:text-6xl">📄</div>

                    {/* Texte */}
                    <p className="text-gray-700 font-medium text-sm sm:text-base">
                      Glissez votre fichier Excel ici
                    </p>
                    <p className="text-gray-500 text-xs sm:text-sm">
                      ou cliquez pour sélectionner un fichier
                    </p>

                    {/* Bouton */}
                    <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm sm:text-base transition">
                      Choisir un fichier
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleExcelUpload}
                        className="hidden"
                      />
                    </label>

                    {/* Info format */}
                    <p className="text-gray-400 text-xs sm:text-sm mt-2">
                      Format accepté : .xlsx ou .xls
                    </p>

                  </div>
                </div>
              </TabPane>
            </TabContent>
          </Col>

          <Col lg={12}>
            <Button
              type="primary"
              htmlType="submit"
              className="mt-3 btn-confirm-reversement"
              loading={isLoadingSubmit}
            >
              Effectuer le paiement
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default PaiementMasse;
